import React, { useEffect, useState } from "react";
import { hasSupabaseConfig, supabase } from "../../lib/supabaseClient";

export default function AdminAccessGate({ children }) {
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState(null);
  const [role, setRole] = useState(null);

  const [mode, setMode] = useState("password"); // password | magic
  const [emailInput, setEmailInput] = useState("");
  const [passwordInput, setPasswordInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (!hasSupabaseConfig || !supabase) {
      setLoading(false);
      setMessage("Falta configurar Supabase para acceso admin.");
      return;
    }

    let mounted = true;

    async function bootstrap() {
      const { data } = await supabase.auth.getSession();
      if (!mounted) return;
      setSession(data?.session || null);
      setLoading(false);
    }

    bootstrap();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      setSession(nextSession || null);
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function checkRole() {
      if (!session?.user?.email || !supabase) {
        setRole(null);
        return;
      }

      const email = session.user.email.toLowerCase();
      const { data, error } = await supabase
        .from("admin_users")
        .select("role,email")
        .ilike("email", email)
        .limit(1)
        .maybeSingle();

      if (cancelled) return;

      if (error || !data?.role) {
        setRole(null);
        return;
      }

      setRole(data.role);
    }

    checkRole();
    return () => {
      cancelled = true;
    };
  }, [session?.user?.email]);

  async function loginWithPassword(e) {
    e.preventDefault();
    setMessage("");

    if (!supabase) return;

    const email = emailInput.trim().toLowerCase();
    if (!email || !passwordInput) {
      setMessage("Completa email y contraseña.");
      return;
    }

    setBusy(true);
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password: passwordInput,
    });
    setBusy(false);

    if (error) {
      setMessage(`No se pudo iniciar sesión: ${error.message}`);
      return;
    }

    setMessage("Sesión iniciada.");
  }

  async function sendMagicLink(e) {
    e.preventDefault();
    setMessage("");

    if (!supabase) return;

    const email = emailInput.trim().toLowerCase();
    if (!email) {
      setMessage("Escribe un email válido.");
      return;
    }

    setBusy(true);
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        emailRedirectTo: `${window.location.origin}/admin/dashboard`,
      },
    });
    setBusy(false);

    if (error) {
      setMessage(`No se pudo enviar magic link: ${error.message}`);
      return;
    }

    setMessage("Te envié un magic link. Abre ese correo y vuelve al dashboard.");
  }

  async function sendResetPassword() {
    setMessage("");
    if (!supabase) return;

    const email = emailInput.trim().toLowerCase();
    if (!email) {
      setMessage("Escribe tu email para recuperar contraseña.");
      return;
    }

    setBusy(true);
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/admin/dashboard`,
    });
    setBusy(false);

    if (error) {
      setMessage(`No se pudo enviar recuperación: ${error.message}`);
      return;
    }

    setMessage("Te envié el correo de recuperación de contraseña.");
  }

  async function logout() {
    if (!supabase) return;
    await supabase.auth.signOut();
    setRole(null);
    setSession(null);
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#06070A] text-white flex items-center justify-center p-6">
        <div className="rounded-xl border border-white/10 bg-[#111722] px-5 py-4">Cargando acceso admin...</div>
      </div>
    );
  }

  if (!session?.user) {
    return (
      <div className="min-h-screen bg-[#06070A] text-white flex items-center justify-center p-6">
        <div className="w-full max-w-md rounded-2xl border border-white/10 bg-[#111722] p-6">
          <h1 className="text-2xl font-bold">Acceso Admin</h1>
          <p className="text-slate-300 mt-2 text-sm">Acceso para equipo por contraseña (recomendado) o magic link.</p>

          <div className="mt-4 grid grid-cols-2 gap-2">
            <button
              onClick={() => setMode("password")}
              className={`rounded-lg px-3 py-2 text-sm border ${mode === "password" ? "bg-cyan-400 text-black border-cyan-300" : "border-white/20 text-slate-200"}`}
            >
              Contraseña
            </button>
            <button
              onClick={() => setMode("magic")}
              className={`rounded-lg px-3 py-2 text-sm border ${mode === "magic" ? "bg-cyan-400 text-black border-cyan-300" : "border-white/20 text-slate-200"}`}
            >
              Magic Link
            </button>
          </div>

          {mode === "password" ? (
            <form onSubmit={loginWithPassword} className="mt-4 space-y-3">
              <input
                type="email"
                required
                value={emailInput}
                onChange={(e) => setEmailInput(e.target.value)}
                placeholder="tu-email@empresa.com"
                className="w-full rounded-lg bg-[#0b1019] border border-white/15 px-3 py-2"
              />
              <input
                type="password"
                required
                value={passwordInput}
                onChange={(e) => setPasswordInput(e.target.value)}
                placeholder="Tu contraseña"
                className="w-full rounded-lg bg-[#0b1019] border border-white/15 px-3 py-2"
              />
              <button disabled={busy} className="w-full rounded-lg bg-cyan-400 text-black font-semibold px-3 py-2 disabled:opacity-60">
                Entrar
              </button>
              <button type="button" onClick={sendResetPassword} disabled={busy} className="w-full rounded-lg border border-white/20 px-3 py-2 text-sm">
                Recuperar contraseña
              </button>
            </form>
          ) : (
            <form onSubmit={sendMagicLink} className="mt-4 space-y-3">
              <input
                type="email"
                required
                value={emailInput}
                onChange={(e) => setEmailInput(e.target.value)}
                placeholder="tu-email@empresa.com"
                className="w-full rounded-lg bg-[#0b1019] border border-white/15 px-3 py-2"
              />
              <button disabled={busy} className="w-full rounded-lg bg-cyan-400 text-black font-semibold px-3 py-2 disabled:opacity-60">
                Enviar Magic Link
              </button>
            </form>
          )}

          {message ? <p className="text-sm text-slate-300 mt-3">{message}</p> : null}
        </div>
      </div>
    );
  }

  if (!role) {
    return (
      <div className="min-h-screen bg-[#06070A] text-white flex items-center justify-center p-6">
        <div className="w-full max-w-lg rounded-2xl border border-amber-400/30 bg-amber-500/10 p-6">
          <h1 className="text-2xl font-bold">Acceso restringido</h1>
          <p className="text-amber-100 mt-2">
            Tu cuenta (<strong>{session.user.email}</strong>) no está habilitada en <code>admin_users</code>.
          </p>
          <p className="text-amber-100 mt-2 text-sm">Pide a un admin que te agregue con rol (viewer/event_manager/super_admin).</p>
          <button onClick={logout} className="mt-4 rounded-lg border border-white/20 px-3 py-2">Cerrar sesión</button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="bg-[#0b1019] border-b border-white/10 px-4 py-2 text-xs text-slate-300 flex justify-between items-center">
        <span>
          Admin: <strong>{session.user.email}</strong> · Rol: <strong>{role}</strong>
        </span>
        <button onClick={logout} className="px-2 py-1 rounded border border-white/20 hover:bg-white/5">Cerrar sesión</button>
      </div>
      {children}
    </div>
  );
}
